import { useCallback, useState } from 'react';
import styles from './AuthPages.module.css';
import authStyles from '../../features/auth/AuthGate.module.css';
import { brandConfig } from '../../config/brands';
import { useAuthBrandingSync, useAuthLogo, useAuthScheme } from '../../hooks/useAuthScheme';
import { GoogleSignInButton } from '../../features/auth/GoogleSignInButton';
import { setStoredToken } from '../../services/apiClient';
import type { GoogleSignInResult } from '../../features/auth/googleSignIn';
import { appPath, termsHref, isExternalTerms } from '../../utils/navigation';

export default function SignupSelectPage() {
  // ライトテーマ (usodeki 等) では認証カードを明色化する。
  // このページは divider / メール登録ボタンを AuthGate.module.css (authStyles) から
  // 借りているため、そちらの .light 系も効かせるよう両モジュールの light を併記する。
  useAuthBrandingSync();
  const isLight = useAuthScheme() === 'light';
  const logo = useAuthLogo();
  const wrapperClass = `${styles.wrapper}${isLight ? ` ${styles.light} ${authStyles.light}` : ''}`;
  const [error, setError] = useState<string | null>(null);

  const handleGoogleSuccess = useCallback((result: GoogleSignInResult) => {
    setStoredToken(result.token);
    if (result.redirect_to) {
      window.location.assign(appPath(result.redirect_to));
    } else if (result.is_new_user) {
      window.location.assign(appPath('/onboarding'));
    } else {
      window.location.assign(appPath('/'));
    }
  }, []);

  return (
    <div className={wrapperClass}>
      <div className={styles.container}>
        <div className={styles.logoContainer}>
          {/* HypeApp 依頼3 (SAITE のみ設定): ロゴクリックでサービスサイトトップへ */}
          {brandConfig.auth.logoHref ? (
            <a href={brandConfig.auth.logoHref} className={styles.logoLink}>
              <img src={logo.src} alt={brandConfig.serviceName} className={styles.logo} />
            </a>
          ) : (
            <img src={logo.src} alt={brandConfig.serviceName} className={styles.logo} />
          )}
        </div>

        <div className={styles.card}>
          <h1 className={styles.title}>アカウント作成</h1>
          <p className={styles.subtitle}>登録方法を選択してください</p>

          <GoogleSignInButton
            partnerCode={brandConfig.id}
            mode="signup"
            intent="signup"
            renderError={false}
            onSuccess={handleGoogleSuccess}
            onError={(err) => setError(err.message || 'Google認証に失敗しました')}
          />
          {error && (
            <div style={{ color: '#b00020', fontSize: 13, marginTop: 8 }}>{error}</div>
          )}

          <div className={authStyles.divider}>
            <span>または</span>
          </div>

          <button
            type="button"
            className={authStyles.emailLoginButton}
            onClick={() => {
              window.location.assign(appPath('/auth/email/signup'));
            }}
          >
            <svg className={authStyles.googleIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2"/>
              <path d="M22 6L12 13L2 6"/>
            </svg>
            <span>メールアドレスで新規登録</span>
          </button>

          <div className={styles.links}>
            <a href={appPath('/')} className={styles.link}>すでにアカウントをお持ちの方はこちら</a>
          </div>
        </div>

        <footer className={styles.footer}>
          <div className={styles.aboutLink}>
            <a href={brandConfig.urls.website} target="_blank" rel="noopener noreferrer">{brandConfig.serviceName}とは</a>
          </div>
          <div className={styles.footerLinks}>
            <a href={brandConfig.urls.privacy} target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>
            <span className={styles.footerDivider}>|</span>
            <a href={termsHref()} {...(isExternalTerms() ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>利用規約</a>
            <span className={styles.footerDivider}>|</span>
            <a href={brandConfig.urls.contact} target="_blank" rel="noopener noreferrer">お問い合わせ</a>
          </div>
        </footer>
      </div>
    </div>
  );
}
